import { isNil } from 'lodash';
import { CSSProperties, useState } from 'react';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { Field } from '@shared/data/entity/fields/Field';
import { useFormInputButtonTarget } from '@shared/scenes/entity/util/useInputButtonTarget';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { SingleComponentForm } from './InputButtonForms/SingleComponentForm';

interface Props {
  component: API.InputButtonComponent;
  inputField: Field | null;
  workflowTemplateId: string;
  updateField: (componentId: string, field: Field | null) => void;
  style?: CSSProperties;
}

export function InputButtonEditField({
  component,
  inputField,
  workflowTemplateId,
  updateField,
  style,
}: Props) {
  const anchorName = getAnchorName('input-button-edit', component.id);
  const [showForm, setShowForm] = useState(false);
  const workflowTemplate = useWorkflowTemplate(workflowTemplateId);
  const target = useFormInputButtonTarget(
    workflowTemplate.workflowTemplate,
    component.inputComponentId,
  );

  const onClick = () => {
    setShowForm(prev => !prev);
  };

  return (
    <View center style={style}>
      {/* On the View because the button is disabled whenever the tooltip shows. */}
      <View
        row
        tooltip={i18n('card.form.inputButton.readOnlyTooltip', { label: target.label })}
        tooltipDisabled={!target.isReadOnly}
      >
        <Button
          variant="secondary"
          label={component.buttonText}
          numberOfLines={1}
          onClick={onClick}
          style={{ anchorName }}
          size="medium"
          disabled={target.isReadOnly}
        />
      </View>
      {showForm && !target.isReadOnly && !isNil(workflowTemplate.workflowTemplate) && (
        <SingleComponentForm
          fieldName={component.inputComponentId}
          field={inputField}
          anchorName={anchorName}
          workflowTemplate={workflowTemplate.workflowTemplate}
          onClose={() => setShowForm(false)}
          loading={workflowTemplate.loading}
          onUpdateField={updateField}
        />
      )}
    </View>
  );
}
